import {useState, useRef} from 'react';
import {CustomerDetails, CustomerSystem, SystemBillingItem} from '@/store/customerFile.ts';
import {BuyUnitsModal} from './modals/BuyUnitsModal.tsx';
import {Badge} from '@/components/ui/badge.tsx';
import {Popover, PopoverContent, PopoverTrigger} from '@/components/ui/popover.tsx';
import {
    ShoppingCartIcon, ArrowsRightLeftIcon,
    QuestionMarkCircleIcon, CogIcon, ArrowPathIcon,
} from '@heroicons/react/24/outline';
import {TransferUnitsModal} from './modals/TransferUnitsModal.tsx';
import {BuyChannelsModal} from './modals/BuyChannelsModal.tsx';

import {ChangeSubscriptionModal} from './modals/ChangeSubscriptionModal.tsx';

interface Props {
    systems: CustomerSystem[];
    customer: CustomerDetails;
}

function fmtCurrency(val: string | number | null | undefined) {
    if (val == null || val === '0') return '₪\u00a00';
    const n = parseFloat(String(val));
    return `₪\u00a0${n.toLocaleString('he-IL', {minimumFractionDigits: 0, maximumFractionDigits: 2})}`;
}

/* ── Phone cell ──────────────────────────────────────── */
function PhoneCell({system}: {system: CustomerSystem}) {
    const [expanded, setExpanded] = useState(false);
    const extraPhones = [...system.other_phones, ...(system.incoming_lines ?? [])];
    const hasMore = extraPhones.length > 0;
    return (
        <div>
            <span className="font-mono text-sm">{system.main_phone ?? '—'}</span>
            {hasMore && (
                <>
                    <button
                        type="button"
                        onClick={() => setExpanded((v) => !v)}
                        className="mr-1 text-xs text-blue-500 hover:underline"
                    >
                        {expanded ? 'הסתר' : `+${extraPhones.length}`}
                    </button>
                    {expanded && (
                        <div className="mt-0.5 space-y-0">
                            {extraPhones.map((p, i) => (
                                <div key={i} className="text-xs font-mono text-gray-500">
                                    {p.number}
                                </div>
                            ))}
                        </div>
                    )}
                </>
            )}
        </div>
    );
}

/* ── Charge detail — shadcn Popover ─────────────────── */
function ChargeDetailDrawer({detail}: {detail: SystemBillingItem[]}) {
    return (
        <Popover>
            <PopoverTrigger className="text-xs text-blue-600 hover:underline">
                פירוט
            </PopoverTrigger>
            <PopoverContent side="top" align="start" className="p-3 text-xs space-y-1 min-w-[200px]">
                {detail.length === 0 ? (
                    <span className="text-gray-400">אין פריטים</span>
                ) : (
                    detail.map((item) => (
                        <div key={item.id} className="flex justify-between gap-3">
                            <span className="text-gray-600">{item.item_type_name}</span>
                            <span className="font-medium">{fmtCurrency(item.total_after_discount)}</span>
                        </div>
                    ))
                )}
            </PopoverContent>
        </Popover>
    );
}

/* ── Shared button tooltip ───────────────────────────── */
function BtnTooltip({label, children}: {label: string; children: React.ReactNode}) {
    return (
        <div className="relative group inline-flex">
            {children}
            <div className="pointer-events-none absolute bottom-full left-1/2 -translate-x-1/2 mb-1.5 z-50 whitespace-nowrap rounded-md bg-gray-800 px-2 py-1 text-xs text-white opacity-0 shadow-md transition-opacity group-hover:opacity-100">
                {label}
            </div>
        </div>
    );
}

/* ── Channels tooltip — fixed positioning to avoid overflow clipping ── */
const CHANNELS_TOOLTIP = 'כל שיחה נכנסת למערכת, נקראת ערוץ. קיימת הגבלה על מספר השיחות הנכנסות בבת אחת, בכל רגע נתון.';
function ChannelsTooltip() {
    const [pos, setPos] = useState<{top: number; left: number} | null>(null);
    const btnRef = useRef<HTMLButtonElement>(null);

    function showTip() {
        if (btnRef.current) {
            const r = btnRef.current.getBoundingClientRect();
            setPos({top: r.bottom + 6, left: r.left + r.width / 2});
        }
    }

    return (
        <>
            <button
                ref={btnRef}
                type="button"
                onMouseEnter={showTip}
                onMouseLeave={() => setPos(null)}
                onClick={() => (pos ? setPos(null) : showTip())}
                className="text-gray-400 hover:text-blue-500 transition-colors"
                aria-label="הסבר על ערוצים"
            >
                <QuestionMarkCircleIcon className="w-3.5 h-3.5" />
            </button>
            {pos && (
                <div
                    style={{position: 'fixed', top: pos.top, left: pos.left, transform: 'translateX(-50%)'}}
                    className="z-50 pointer-events-none bg-gray-800 text-white text-xs rounded-lg px-3 py-2 max-w-[220px] leading-snug shadow-lg"
                >
                    {CHANNELS_TOOLTIP}
                </div>
            )}
        </>
    );
}

/* ── System row ───────────────────────────────────────── */
function SystemRow({system, customer}: {system: CustomerSystem; customer: CustomerDetails}) {
    const [transferOpen, setTransferOpen] = useState(false);
    const [buyChannelsOpen, setBuyChannelsOpen] = useState(false);
    const [changeSubOpen, setChangeSubOpen] = useState(false);
    const [buyUnitsOpen, setBuyUnitsOpen] = useState(false);

    return (
        <>
            <tr className="border-b border-gray-50 hover:bg-gray-50/50">
                {/* שם מערכת */}
                <td className="py-3 px-3">
                    <div className="flex items-center gap-2">
                        <span className="font-medium text-sm text-gray-900">{system.name}</span>
                        {!system.active && <Badge variant="secondary" className="text-xs">לא פעיל</Badge>}
                    </div>
                </td>

                {/* טלפונים */}
                <td className="py-3 px-3"><PhoneCell system={system} /></td>

                {/* מסלול חיוב */}
                <td className="py-3 px-3">
                    <div className="flex items-center gap-1.5 flex-wrap">
                        <span className="text-sm">{system.subscription_name ?? '—'}</span>
                        <BtnTooltip label="שנה מסלול">
                            <button
                                type="button"
                                onClick={() => setChangeSubOpen(true)}
                                className="inline-flex items-center justify-center w-6 h-6 rounded-md border border-gray-200 text-gray-500 hover:text-amber-600 hover:border-amber-300 transition-colors"
                            >
                                <ArrowPathIcon className="w-3.5 h-3.5" />
                            </button>
                        </BtnTooltip>
                    </div>
                </td>

                {/* חיוב חודשי — detail floats, no row jump */}
                <td className="py-3 px-3 whitespace-nowrap">
                    <div className="text-sm font-medium">{fmtCurrency(system.monthly_charge)}</div>
                    {system.monthly_charge_detail.length > 0 && (
                        <ChargeDetailDrawer detail={system.monthly_charge_detail} />
                    )}
                </td>

                {/* ערוצים */}
                <td className="py-3 px-3">
                    <div className="flex items-center gap-1 flex-wrap">
                        <span className="text-sm">{(system.amount_of_lines ) <= 0 ? '-' : system.amount_of_lines}</span>
                        <BtnTooltip label="רכישת ערוצים">
                            <button
                                type="button"
                                onClick={() => setBuyChannelsOpen(true)}
                                className="inline-flex items-center justify-center w-6 h-6 rounded-md border border-gray-200 text-gray-500 hover:text-blue-600 hover:border-blue-300 transition-colors"
                            >
                                <ShoppingCartIcon className="w-3.5 h-3.5" />
                            </button>
                        </BtnTooltip>
                    </div>
                </td>

                {/* יתרת יחידות */}
                <td className="py-3 px-3 whitespace-nowrap">
                    <div className="flex items-center gap-1.5">
                        <span className="text-sm font-medium">{system.balance_unit.toLocaleString('he-IL', {minimumFractionDigits: 1, maximumFractionDigits: 1})}</span>
                        <BtnTooltip label="רכישת יחידות">
                            <button
                                type="button"
                                onClick={() => setBuyUnitsOpen(true)}
                                className="inline-flex items-center justify-center w-6 h-6 rounded-md border border-gray-200 text-gray-500 hover:text-orange-500 hover:border-orange-300 transition-colors"
                            >
                                <ShoppingCartIcon className="w-3.5 h-3.5" />
                            </button>
                        </BtnTooltip>
                        <BtnTooltip label="העברת יחידות">
                            <button
                                type="button"
                                onClick={() => setTransferOpen(true)}
                                className="inline-flex items-center justify-center w-6 h-6 rounded-md border border-gray-200 text-gray-500 hover:text-emerald-600 hover:border-emerald-300 transition-colors"
                            >
                                <ArrowsRightLeftIcon className="w-3.5 h-3.5" />
                            </button>
                        </BtnTooltip>
                    </div>
                </td>

                {/* פעולות */}
                <td className="py-3 px-3">
                    <BtnTooltip label="הגדרות מערכת">
                        <a
                            href={`/learning/school/${system.id}/`}
                            className="inline-flex items-center justify-center w-6 h-6 rounded-md border border-gray-200 text-gray-500 hover:text-gray-900 hover:border-gray-300 transition-colors"
                        >
                            <CogIcon className="w-3.5 h-3.5" />
                        </a>
                    </BtnTooltip>
                </td>
            </tr>

            <TransferUnitsModal open={transferOpen} onClose={() => setTransferOpen(false)} fromSystem={system} />
            <BuyChannelsModal open={buyChannelsOpen} onClose={() => setBuyChannelsOpen(false)} system={system} />
            <ChangeSubscriptionModal open={changeSubOpen} onClose={() => setChangeSubOpen(false)} system={system} />
            <BuyUnitsModal open={buyUnitsOpen} onClose={() => setBuyUnitsOpen(false)} systems={[system]} customer={customer} currentSystemId={system.id} />
        </>
    );
}

export function CustomerFileSystems({systems, customer}: Props) {
    const activeSystems = systems.filter((s) => s.active);
    const inactiveSystems = systems.filter((s) => !s.active);
    const displaySystems = [...activeSystems, ...inactiveSystems];

    return (
        <div>
            {displaySystems.length === 0 ? (
                <p className="text-sm text-gray-400 py-6 text-center">אין מערכות מקושרות</p>
            ) : (
                <div className="overflow-x-auto">
                    <table className="w-full text-right text-sm">
                        <thead>
                        <tr className="border-b border-gray-100">
                            <th className="py-2 px-3 text-xs font-medium text-gray-500 text-right">שם מערכת</th>
                            <th className="py-2 px-3 text-xs font-medium text-gray-500 text-right">טלפונים</th>
                            <th className="py-2 px-3 text-xs font-medium text-gray-500 text-right">מסלול חיוב</th>
                            <th className="py-2 px-3 text-xs font-medium text-gray-500 text-right">חיוב חודשי</th>
                            <th className="py-2 px-3 text-xs font-medium text-gray-500 text-right flex items-center gap-1">
                                ערוצים
                        
                                <ChannelsTooltip />
                            
                            </th>
                            <th className="py-2 px-3 text-xs font-medium text-gray-500 text-right">יתרת יחידות</th>
                            <th className="py-2 px-3 text-xs font-medium text-gray-500 text-right">פעולות</th>
                        </tr>
                        </thead>
                        <tbody>
                        {displaySystems.map((system) => (
                            <SystemRow key={system.id} system={system} customer={customer} />
                        ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}
